import Tool, { type ToolArgsWithController } from '../../../tools/Tool';
import { getTableStructureFromElement } from '../TableStructure';
import TableUtils from '../TableUtils';
import { Editor, Transforms } from 'slate';
import deleteColumnSvg from '../../../assets/icons/deleteColumn.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';

class ToolDeleteColumn extends Tool {
  static toolName = 'Delete column';

  static renderToolbarButtonIcon(): JSX.Element {
    return svgToIcon(deleteColumnSvg);
  }

  static isEnabled({ editor, controller }: ToolArgsWithController) {
    const currentTable = TableUtils.currentTable(editor);
    if (!currentTable) return false;

    const tableStructure = getTableStructureFromElement(
      editor,
      currentTable[0]
    );
    if (!tableStructure) return false;

    // Don't allow delete if there's only one column
    if (tableStructure.width === 1) return false;

    const tableSelection = controller.state.pluginState.tableSelection;
    if (!tableSelection) return false;

    if (tableSelection.multipleCellsSelected) {
      return tableSelection.width < tableStructure.width;
    }

    return true;
  }

  static action({ editor, controller }: ToolArgsWithController): void {
    if (!editor?.selection) return;

    const currentTable = TableUtils.currentTable(editor);
    if (!currentTable) return;

    const tableStructure = getTableStructureFromElement(
      editor,
      currentTable[0]
    );
    if (!tableStructure) return;
    if (tableStructure.width === 1) return;

    const tableSelection = controller.state.pluginState.tableSelection;
    if (!tableSelection) return;

    let fromIndex = -1;
    let toIndex = tableStructure.width;

    let selectedRowIndex = 0;
    if (tableSelection.multipleCellsSelected) {
      const cellPaths = Array.from(tableSelection.selectedCellPaths);

      [fromIndex, toIndex, selectedRowIndex] = cellPaths.reduce(
        ([min, max, rowIndex], path) => {
          const cell = tableStructure.pathToCoordinates(path);
          if (!cell) return [min, max, rowIndex];

          return [
            Math.min(min, cell.columnIndex),
            Math.max(max, cell.columnIndex),
            Math.min(rowIndex, cell.rowIndex),
          ];
        },
        [toIndex, fromIndex, tableStructure.height]
      );
    } else {
      const path = TableUtils.currentCellPath(editor);
      if (!path) return;

      const cell = tableStructure.pathToCoordinates(path.slice(-2));
      if (!cell) return;
      selectedRowIndex = cell.rowIndex;

      fromIndex = path[path.length - 1];
      toIndex = path[path.length - 1];
    }

    if (fromIndex < 0 || toIndex >= tableStructure.width) return;

    for (let i = toIndex; i >= fromIndex; i--) {
      TableUtils.deleteColumn(editor, i);
    }

    const deletedLastColumn = toIndex === tableStructure.width - 1;
    const newColumnIndex = deletedLastColumn ? fromIndex - 1 : fromIndex;

    // We need to interact with the updated table/structure
    TableUtils.withCurrentTableStructure(
      editor,
      (currentTable, tableStructure) => {
        const cellInfo = tableStructure.cell(newColumnIndex, selectedRowIndex);

        if (cellInfo) {
          Transforms.select(
            editor,
            Editor.point(editor, [...currentTable[1], ...cellInfo.relativePath])
          );
        }
      }
    );
  }
}

export default ToolDeleteColumn;
